import React, { useEffect, useImperativeHandle, useRef } from 'react';

const SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

export const TURNSTILE_ENABLED = true;

export interface TurnstileHandle {
  reset: () => void;
}

interface TurnstileProps {
  siteKey?: string;
  language?: string;
  onSuccess: (token: string) => void;
  onExpire: () => void;
  className?: string;
}

type TurnstileApi = {
  render: (element: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

const turnstileApi = (): TurnstileApi | undefined =>
  (window as Window & { turnstile?: TurnstileApi }).turnstile;

const Turnstile = React.forwardRef<TurnstileHandle, TurnstileProps>(
  ({ siteKey, language, onSuccess, onExpire, className }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | undefined>(undefined);
    const onSuccessRef = useRef(onSuccess);
    const onExpireRef = useRef(onExpire);

    onSuccessRef.current = onSuccess;
    onExpireRef.current = onExpire;

    useImperativeHandle(ref, () => ({
      reset: () => {
        const api = turnstileApi();
        if (widgetIdRef.current !== undefined && api) {
          api.reset(widgetIdRef.current);
        }
      },
    }));

    useEffect(() => {
      if (!siteKey) return undefined;

      let cancelled = false;

      const clearWidget = () => {
        const api = turnstileApi();
        if (widgetIdRef.current !== undefined && api) {
          api.remove(widgetIdRef.current);
        }
        widgetIdRef.current = undefined;
      };

      const render = () => {
        const api = turnstileApi();
        if (
          cancelled ||
          !containerRef.current ||
          !api ||
          widgetIdRef.current !== undefined
        ) {
          return;
        }
        const code = (language || 'auto').split('-')[0];
        widgetIdRef.current = api.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'light',
          size: 'normal',
          language: code,
          appearance: 'always',
          execution: 'render',
          retry: 'auto',
          'retry-interval': 8000,
          'refresh-expired': 'auto',
          'refresh-timeout': 'auto',
          callback: (token: string) => onSuccessRef.current(token),
          'expired-callback': () => onExpireRef.current(),
          'timeout-callback': () => onExpireRef.current(),
          'error-callback': () => onExpireRef.current(),
        });
      };

      if (turnstileApi()) {
        render();
      } else {
        let script = document.getElementById(
          SCRIPT_ID,
        ) as HTMLScriptElement | null;
        if (!script) {
          script = document.createElement('script');
          script.id = SCRIPT_ID;
          script.src = SCRIPT_URL;
          script.async = true;
          script.defer = true;
          document.head.appendChild(script);
        }
        script.addEventListener('load', render);
        if (turnstileApi()) render();
        return () => {
          cancelled = true;
          script?.removeEventListener('load', render);
          clearWidget();
        };
      }

      return () => {
        cancelled = true;
        clearWidget();
      };
    }, [siteKey, language]);

    return <div ref={containerRef} className={className} />;
  },
);

Turnstile.displayName = 'Turnstile';

export default Turnstile;
